/**
 * Lot −20 % depuis le configurateur classique (décisions de Theo, 19/10/2026).
 *
 * La cliente garde tout le sac qu'elle vient de composer (corps, rabat, fermoir, sangle, charm), ajoute 2 rabats,
 * puis éventuellement des accessoires : tout passe à −20 %, comme le lot. Un encart en fin de configurateur et un
 * rappel au-dessus d'« Acheter » ouvrent un panneau plein écran : 2e rabat → 3e rabat → accessoires → récap.
 * L'ajout au panier passe par fc_add_bundle_to_cart en mode « sac du configurateur » (config_slug + config_wc_ids) :
 * mêmes lignes groupées, même remise, même livraison offerte que le lot.
 *
 * Les rabats, les accessoires et la remise viennent du lot de la même taille (LOT_BY_SIZE).
 */
import { useEffect, useMemo, useState } from 'react'
import { ConfigProduct, useFunnelConfig } from '@/lib/hooks/useFunnelConfig'
import type { ConfiguratorSlotContext } from '@/components/Configurator'
import { ct, cterm, cmoney } from '@/lib/i18n/configurator'

const LOT_BY_SIZE: Record<string, string> = { s: 'lot-sac-et-rabats', m: 'lot-sac-et-rabats-equilibre', l: 'lot-sac-et-rabats-intégral' }

type Tracker = (...args: unknown[]) => void
function track(name: string, params: Record<string, unknown>) {
  const w = window as unknown as { gtag?: Tracker; fbq?: Tracker }
  try { w.gtag?.('event', name, params) } catch { /* suivi indisponible */ }
  try { w.fbq?.('trackCustom', name, params) } catch { /* suivi indisponible */ }
}

// Montant rond sans « ,00 » (prix rond du lot : « 151 € »), les autres gardent leurs centimes
const fmt = (cents: number) => (Math.round(cents) % 100 === 0 ? cmoney(cents).replace(/[.,]00(?!\d)/, '') : cmoney(cents))
// Prix rond du lot (demande de Theo, 31/10/2026) : sac + rabats remisés arrondis à l'euro le plus proche, comme le
// panier (bundle.php, funnel_bundle_apply_per_line_discount) ; les accessoires gardent leurs centimes.
const roundEuro = (c: number) => Math.round(c / 100) * 100
const shortName = (n: string) => n.replace(/^Rabat\s*-\s*/i, '').trim()
// Nom affiché dans la langue de la page ; les noms d'origine (français) restent ceux envoyés au panier et au suivi
const label = (n: string) => cterm(shortName(n))

export function useLotUpsell(ctx: ConfiguratorSlotContext | null) {
  const lotSlug = ctx?.size ? LOT_BY_SIZE[ctx.size] : ''
  const { config: lot } = useFunnelConfig(lotSlug || '')
  return useMemo(() => {
    if (!lot || lot.type !== 'bundle' || !lot.bundleConfig || !ctx) return null
    const D = lot.bundleConfig.discountPercent || 0
    const off = (c: number) => Math.round(c * (1 - D / 100))
    const flapCatId = lot.bundleConfig.slots[0]?.sourceCategoryId
    const flapCat = lot.categories.find(c => c.id === flapCatId)
    const flaps = (flapCat?.products || []).filter(p => p.wcId)
    const flapIds = new Set(flaps.map(p => p.wcId))
    const myFlap = ctx.selectedProducts.find(p => p.wcId && flapIds.has(p.wcId)) || null
    const extras = lot.categories
      .filter(c => c.id !== flapCatId)
      .map(c => ({ id: c.id, name: c.name, products: c.products.filter(p => p.wcId && p.price > 0) }))
      .filter(c => c.products.length)
      .sort((a, b) => Number(b.name === 'Portefeuille') - Number(a.name === 'Portefeuille')) // comme le lot
    // Prix du sac composé : base + produits choisis, ligne par ligne (la remise s'applique ainsi au panier)
    const basePrice = ctx.totalPrice - ctx.selectedProducts.reduce((a, p) => a + p.price, 0)
    const bagNow = off(basePrice) + ctx.selectedProducts.reduce((a, p) => a + off(p.price), 0)
    return { lot, lotSlug, D, off, flaps, myFlap, extras, basePrice, bagNow }
  }, [lot, lotSlug, ctx])
}

export type LotUpsellData = NonNullable<ReturnType<typeof useLotUpsell>>

const CSS = `
/* le thème (Breakdance) centre le texte : on remet tout à gauche */
.lu-card, .lu-ov, .lu-cmp, .lu-mini, .lu-recap, .lu-line, .lu-tot { text-align:left; }
.lu-q, .lu-qs, .lu-opt, .lu-done, .lu-slot .lbl { text-align:center; }
.lu-card { background:#f6f1ec; border-radius:18px; padding:16px 14px 14px; margin-top:4px; font-family:inherit; color:#1a1a1a; }
.lu-eyebrow { display:inline-block; font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:#fff; background:#9b2b2a; border-radius:6px; padding:3px 8px; }
.lu-card h4 { margin:8px 0 4px; font-size:17px; line-height:1.25; font-weight:700; }
.lu-card p { margin:0 0 10px; font-size:13px; line-height:1.45; color:#4a4441; }
.lu-trio { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; margin-bottom:10px; }
.lu-slot { background:#fff; border-radius:12px; aspect-ratio:1/.85; display:grid; place-items:center; position:relative; overflow:hidden; }
.lu-slot img { width:84%; height:62%; object-fit:contain; margin-top:-12px; }
.lu-slot .lbl { position:absolute; bottom:5px; left:0; right:0; text-align:center; font-size:10px; font-weight:600; color:#6b6461; }
.lu-slot.empty { border:1.5px dashed #c9bfb6; background:transparent; font:inherit; color:inherit; padding:0; width:100%; cursor:pointer; transition:border-color .15s, background .15s; }
.lu-slot.empty:hover, .lu-slot.empty:focus-visible { border-color:#9b2b2a; background:#fff; outline:none; } .lu-slot.empty .plus { font-size:26px; color:#a89c92; margin-top:-14px; }
.lu-cmp { display:grid; grid-template-columns:1fr auto; gap:4px 10px; font-size:13px; background:#fff; border-radius:12px; padding:10px 12px; margin-bottom:10px; }
.lu-cmp .v { text-align:right; font-weight:600; white-space:nowrap; } .lu-cmp .lot { font-weight:700; } .lu-cmp .lot.v { color:#9b2b2a; }
.lu-cmp s { color:#6b6461; font-weight:400; margin-right:6px; } .lu-cmp .note { grid-column:1/-1; font-size:11.5px; color:#6b6461; }
.lu-btn { display:flex; align-items:center; justify-content:center; gap:8px; width:100%; border:0; border-radius:10px; background:#1a1a1a; color:#fff; font:inherit; font-size:15px; font-weight:600; padding:13px; cursor:pointer; }
.lu-btn.accent { background:#9b2b2a; } .lu-btn:disabled { opacity:.45; cursor:default; }
.lu-btn.ghost { background:#fff; color:#1a1a1a; border:1.5px solid #1a1a1a; }
.lu-hint { display:flex; align-items:center; justify-content:center; gap:6px; width:100%; background:none; border:0; font:inherit; font-size:12.5px; font-weight:500; color:#9b2b2a; margin:0 0 8px; text-decoration:underline; text-underline-offset:3px; cursor:pointer; }
.lu-hint b { background:#9b2b2a; color:#fff; border-radius:5px; padding:1px 6px; text-decoration:none; font-size:11px; }
.lu-ov { position:fixed; inset:0; z-index:2147483000; background:#fff; display:flex; flex-direction:column; font-family:inherit; color:#1a1a1a; }
@media (min-width:1024px) { .lu-ov { inset:auto; top:50%; left:50%; width:min(720px,94vw); height:min(820px,92vh); transform:translate(-50%,-50%); border-radius:20px; box-shadow:0 30px 80px rgba(0,0,0,.35); overflow:hidden; } .lu-back { position:fixed; inset:0; z-index:2147482999; background:rgba(0,0,0,.45); } }
.lu-top { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; border-bottom:1px solid #e7dfd8; font-size:13px; }
.lu-top b { font-size:15px; } .lu-x { width:34px; height:34px; border-radius:50%; background:#f6f1ec; border:0; font-size:20px; line-height:1; cursor:pointer; }
.lu-mini { display:flex; gap:10px; align-items:center; padding:10px 14px; background:#f6f1ec; font-size:12px; color:#4a4441; }
.lu-mini b { color:#1a1a1a; }
.lu-body { flex:1; overflow-y:auto; padding:0 14px 16px; }
/* aperçu du sac composé avec le rabat essayé, collé en haut pendant le défilement des rabats */
.lu-prev { position:sticky; top:0; z-index:2; background:#fff; margin:0 -14px; padding:6px 14px 4px; border-bottom:1px solid #f0ebe6; }
.lu-stage { position:relative; width:100%; max-width:420px; margin:0 auto; aspect-ratio:1200/711; }
.lu-stage img { position:absolute; inset:0; width:100%; height:100%; object-fit:contain; }
.lu-prev .cap { text-align:center; font-size:12px; color:#4a4441; margin:2px 0 0; min-height:16px; } .lu-prev .cap b { color:#1a1a1a; }
@media (min-width:1024px) { .lu-stage { max-width:380px; } }
.lu-q { text-align:center; font-size:19px; font-weight:700; margin:14px 0 2px; } .lu-qs { text-align:center; font-size:12.5px; color:#6b6461; margin:0 0 12px; }
.lu-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px; }
@media (min-width:600px) { .lu-grid { grid-template-columns:repeat(4,minmax(0,1fr)); } }
.lu-opt { border:1.5px solid #e7dfd8; border-radius:12px; padding:6px 4px 7px; background:#fff; text-align:center; font:inherit; font-size:11.5px; font-weight:500; color:#1a1a1a; cursor:pointer; position:relative; }
.lu-opt img { width:92%; aspect-ratio:1.25; object-fit:contain; display:block; margin:0 auto 2px; }
.lu-opt .pr { display:block; font-size:11px; color:#9b2b2a; font-weight:600; margin-top:1px; } .lu-opt .pr s { color:#9a918c; font-weight:400; margin-right:4px; }
.lu-opt[aria-pressed="true"] { border-color:#1a1a1a; box-shadow:0 0 0 1.5px #1a1a1a; }
.lu-opt:disabled { opacity:.4; cursor:default; } .lu-opt .tag { position:absolute; top:4px; left:4px; right:4px; background:#fff; border-radius:6px; font-size:9.5px; font-weight:600; padding:2px; }
.lu-opt .chk { position:absolute; top:5px; right:5px; width:18px; height:18px; border-radius:50%; border:1.5px solid #c9bfb6; background:#fff; font-size:11px; line-height:15px; color:#fff; }
.lu-opt[aria-pressed="true"] .chk { background:#1a1a1a; border-color:#1a1a1a; }
.lu-tabs { display:flex; gap:6px; overflow-x:auto; padding:2px 0 10px; scrollbar-width:none; }
.lu-tab { flex:none; border:1.5px solid #e7dfd8; background:#fff; border-radius:999px; padding:7px 13px; font:inherit; font-size:12.5px; font-weight:500; cursor:pointer; white-space:nowrap; }
.lu-tab[aria-pressed="true"] { background:#1a1a1a; color:#fff; border-color:#1a1a1a; } .lu-tab .n { margin-left:5px; font-size:11px; opacity:.8; }
.lu-recap { display:grid; gap:8px; font-size:13px; margin-top:12px; }
.lu-line { display:flex; justify-content:space-between; align-items:center; gap:10px; border-bottom:1px solid #e7dfd8; padding-bottom:7px; }
.lu-line > span:first-child { display:flex; gap:9px; align-items:center; min-width:0; } .lu-line img { width:40px; height:40px; object-fit:contain; background:#f6f1ec; border-radius:8px; flex:none; }
.lu-line .k { display:block; font-size:10.5px; color:#6b6461; } .lu-line .p { white-space:nowrap; font-weight:600; } .lu-line .p s { color:#9a918c; font-weight:400; font-size:11.5px; margin-right:4px; }
.lu-rm { border:0; background:none; color:#6b6461; font:inherit; font-size:11px; text-decoration:underline; cursor:pointer; padding:0; margin-left:6px; }
.lu-tot { background:#f6f1ec; border-radius:12px; padding:10px 12px; font-size:13px; display:grid; gap:3px; margin-top:4px; }
.lu-tot .big { font-size:18px; font-weight:700; color:#9b2b2a; }
.lu-foot { border-top:1px solid #e7dfd8; padding:10px 14px 14px; display:grid; gap:8px; background:#fff; }
.lu-foot .row { display:flex; gap:8px; } .lu-foot .row .lu-btn { flex:1; }
.lu-done { text-align:center; padding:40px 18px; } .lu-done .ok { width:56px; height:56px; border-radius:50%; background:#9b2b2a; color:#fff; display:grid; place-items:center; font-size:28px; margin:0 auto 14px; }
.lu-done h3 { margin:0 0 6px; font-size:20px; } .lu-done p { margin:0 0 18px; color:#4a4441; font-size:14px; }
`

/** Encart en fin de configurateur (rendu dans le flux du configurateur). */
export function LotUpsellCard({ ctx, data, onOpen }: { ctx: ConfiguratorSlotContext; data: LotUpsellData | null; onOpen: () => void }) {
  if (!data || !ctx.isValid || !data.myFlap) return null
  const ref = data.flaps.find(f => f.id !== data.myFlap!.id) || data.myFlap
  const extra2 = 2 * ref.price
  const lotNow = roundEuro(data.bagNow + 2 * data.off(ref.price))
  return (
    <div className="lu-card">
      <style>{CSS}</style>
      <span className="lu-eyebrow">{ct('luEyebrow', { d: data.D })}</span>
      <h4>{ct('luTitle')}</h4>
      <p>{ct('luText', { d: data.D })}</p>
      <div className="lu-trio">
        <div className="lu-slot">{data.myFlap.thumb && <img src={data.myFlap.thumb} alt="" />}<span className="lbl">{ct('luYourFlap')}</span></div>
        {/* les « + » ouvrent aussi le choix des rabats : c'est là qu'on clique naturellement (Theo, 19/10/2026) */}
        <button type="button" className="lu-slot empty" onClick={onOpen} aria-label={ct('luChoose2')}><span className="plus">+</span><span className="lbl">{ct('luFlap2')}</span></button>
        <button type="button" className="lu-slot empty" onClick={onOpen} aria-label={ct('luChoose3')}><span className="plus">+</span><span className="lbl">{ct('luFlap3')}</span></button>
      </div>
      <div className="lu-cmp">
        <span>{ct('luBag1')}</span><span className="v">{fmt(ctx.totalPrice)}</span>
        <span className="lot">{ct('luBag3')}</span><span className="v lot"><s>{fmt(ctx.totalPrice + extra2)}</s>{fmt(lotNow)}</span>
        <span className="note">{ct('luNote', { price: fmt(Math.max(0, lotNow - ctx.totalPrice)) })}</span>
      </div>
      <button type="button" className="lu-btn" onClick={onOpen}>{ct('luCta')}</button>
    </div>
  )
}

/** Rappel au-dessus du bouton « Acheter ». */
export function LotUpsellHint({ ctx, data, onOpen }: { ctx: ConfiguratorSlotContext; data: LotUpsellData | null; onOpen: () => void }) {
  if (!data || !ctx.isValid || !data.myFlap) return null
  return (
    <button type="button" className="lu-hint" onClick={onOpen}>
      <style>{CSS}</style>
      <b>{ct('luPct', { d: data.D })}</b> {ct('luHint')}
    </button>
  )
}

type Step = 'flap1' | 'flap2' | 'extras' | 'recap' | 'done'

/** Panneau plein écran : 2e rabat → 3e rabat → accessoires → récapitulatif → ajout au panier. */
export function LotUpsellPanel({ ctx, data, configSlug, wcAjaxUrl, wcNonce, onClose }: {
  ctx: ConfiguratorSlotContext; data: LotUpsellData; configSlug: string; wcAjaxUrl?: string; wcNonce?: string; onClose: () => void
}) {
  const [step, setStep] = useState<Step>('flap1')
  const [picks, setPicks] = useState<(ConfigProduct | null)[]>([null, null])
  const [extras, setExtras] = useState<{ catId: string; p: ConfigProduct }[]>([])
  const [tab, setTab] = useState(data.extras[0]?.id || '')
  const [busy, setBusy] = useState(false)
  const { off, D, myFlap } = data

  // Panneau ouvert : les boutons flottants du site (langue TrueLang, bulle du mini-panier) passeraient par-dessus
  // ses boutons ; on les masque, et la page derrière ne défile plus
  useEffect(() => {
    if (!document.getElementById('fc-lot-open-style')) {
      const st = document.createElement('style')
      st.id = 'fc-lot-open-style'
      st.textContent = 'body.fc-lot-open #truelang-switcher, body.fc-lot-open .xoo-wsc-basket, body.fc-lot-open #fc-exit-tab { display: none !important; } body.fc-lot-open { overflow: hidden !important; }'
      document.head.appendChild(st)
    }
    document.body.classList.add('fc-lot-open')
    return () => document.body.classList.remove('fc-lot-open')
  }, [])

  const flapLines = picks.filter(Boolean).map(p => p!.price)
  const extraLines = extras.map(e => e.p.price)
  const before = ctx.totalPrice + [...flapLines, ...extraLines].reduce((a, c) => a + c, 0)
  // Sac + rabats au prix rond (écart reporté sur la ligne du sac), puis les accessoires remisés à part
  const coreExact = data.bagNow + flapLines.reduce((a, c) => a + off(c), 0)
  const coreDelta = roundEuro(coreExact) - coreExact
  const now = coreExact + coreDelta + extraLines.reduce((a, c) => a + off(c), 0)

  const k = step === 'flap1' ? 0 : 1

  // Aperçu : son sac tel que composé, avec le rabat de l'étape (ou le sien tant qu'elle n'a rien choisi).
  // Images et calques du configurateur lui-même (ses rabats sont les mêmes produits que ceux du lot, par wcId),
  // pour que le fermoir reste au-dessus du rabat comme dans le configurateur.
  const cfgFlapByWc = useMemo(() => {
    const m = new Map<number, ConfigProduct>()
    for (const c of ctx.categories || []) if (/rabat/i.test(c.name)) for (const p of c.products) if (p.wcId) m.set(p.wcId, p)
    return m
  }, [ctx.categories])
  const previewFlap = picks[k] ? cfgFlapByWc.get(picks[k]!.wcId!) || null : null
  const myCfgFlap = myFlap ? cfgFlapByWc.get(myFlap.wcId!) || myFlap : null
  const layers = [
    ...(ctx.baseImage ? [{ src: ctx.baseImage, z: 0 }] : []),
    ...ctx.selectedProducts
      .map(p => (previewFlap && myCfgFlap && p.wcId === myCfgFlap.wcId ? previewFlap : p))
      .filter(p => p.image)
      .map(p => ({ src: p.image, z: p.layer })),
  ].sort((a, b) => a.z - b.z)
  const clipIds = new Set((ctx.categories || []).filter(c => /fermoir/i.test(c.name)).flatMap(c => c.products.map(p => p.id)))
  const clip = ctx.selectedProducts.find(p => clipIds.has(p.id))
  const preview = (
    <div className="lu-prev">
      <div className="lu-stage">{layers.map((l, i) => <img key={i + l.src} src={l.src} alt="" style={{ zIndex: l.z }} />)}</div>
      <p className="cap">{picks[k] ? <><b>{label(picks[k]!.name)}</b>{clip ? ct('luWithClip', { name: cterm(clip.name) }) : ''}</> : ct('luTouch')}</p>
    </div>
  )
  const taken = (p: ConfigProduct) => p.id === myFlap?.id || picks.some((x, j) => j !== k && x?.id === p.id)

  async function addToCart() {
    if (!picks[0] || !picks[1]) return
    if (!wcAjaxUrl || !wcNonce) { console.log('[LotUpsell] hors WordPress', { picks, extras }); return }
    setBusy(true)
    const ids = [ctx.baseWcId, ...ctx.selectedProducts.map(p => p.wcId)].filter((x): x is number => !!x)
    const fd = new FormData()
    fd.append('action', 'fc_add_bundle_to_cart')
    fd.append('nonce', wcNonce)
    fd.append('bundle_slug', data.lotSlug)
    fd.append('bundle_name', data.lot.name)
    fd.append('config_slug', configSlug)
    ids.forEach(id => { fd.append('config_wc_ids[]', String(id)); fd.append('base_wc_ids[]', String(id)) })
    data.lot.bundleConfig!.slots.slice(0, 2).forEach((slot, i) => {
      fd.append(`slot_picks[${i}][slot_id]`, slot.id)
      fd.append(`slot_picks[${i}][slot_name]`, slot.name)
      fd.append(`slot_picks[${i}][product_wc_id]`, String(picks[i]!.wcId))
    })
    extras.forEach((e, i) => {
      const cat = data.extras.find(c => c.id === e.catId)
      fd.append(`extras[${i}][category_id]`, e.catId)
      fd.append(`extras[${i}][category_name]`, cat?.name || '')
      fd.append(`extras[${i}][product_wc_id]`, String(e.p.wcId))
    })
    try {
      const res = await fetch(wcAjaxUrl, { method: 'POST', body: fd, credentials: 'same-origin' })
      const j = await res.json()
      if (!j.success) throw new Error(j.message || ct('luCartError'))
      if (j.warning) alert(j.warning)
      const value = now / 100
      track('config_lot_ajout_panier', { configurateur: configSlug, valeur: value, accessoires: extras.length })
      const w = window as unknown as { gtag?: Tracker; fbq?: Tracker; jQuery?: (el: unknown) => { trigger: (e: string) => void } }
      try { w.gtag?.('event', 'add_to_cart', { currency: 'EUR', value, items: [{ item_id: data.lotSlug, item_name: data.lot.name, item_category: 'Lot', item_variant: 'configurateur', price: value, quantity: 1 }] }) } catch { /* */ }
      try { w.fbq?.('track', 'AddToCart', { currency: 'EUR', value, content_name: data.lot.name, content_type: 'product', content_ids: [data.lotSlug] }) } catch { /* */ }
      if (w.jQuery) { w.jQuery(document.body).trigger('wc_fragment_refresh'); w.jQuery(document.body).trigger('added_to_cart') }
      setStep('done')
    } catch (err) {
      alert((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const title = step === 'flap1' ? ct('luStep', { n: 1 }) : step === 'flap2' ? ct('luStep', { n: 2 }) : step === 'extras' ? ct('luStep', { n: 3 }) : step === 'recap' ? ct('luRecap') : ''
  const flapGrid = (
    <div className="lu-grid">
      {data.flaps.map(p => {
        const t = taken(p)
        return (
          <button key={p.id} type="button" className="lu-opt" disabled={t} aria-pressed={picks[k]?.id === p.id}
            onClick={() => setPicks(prev => { const n = [...prev]; n[k] = p; return n })}>
            {t && <span className="tag">{p.id === myFlap?.id ? ct('luTag1') : ct('luTaken')}</span>}
            {p.thumb && <img src={p.thumb} alt="" loading="lazy" />}
            {label(p.name)}
            <span className="pr"><s>{fmt(p.price)}</s>{fmt(off(p.price))}</span>
          </button>
        )
      })}
    </div>
  )
  const cat = data.extras.find(c => c.id === tab)

  return (
    <>
      <style>{CSS}</style>
      <div className="lu-back" onClick={onClose} />
      <div className="lu-ov" role="dialog" aria-modal="true" aria-label={ct('luLot', { d: D })}>
        <div className="lu-top"><span>{title}</span><b>{ct('luLot', { d: D })}</b><button type="button" className="lu-x" aria-label={ct('close')} onClick={onClose}>×</button></div>
        {step !== 'done' && (
          <div className="lu-mini"><span><b>{ct('luKept')}</b><br />{[cterm(ctx.baseName), ...ctx.selectedProducts.map(p => label(p.name))].filter(Boolean).join(' · ')}</span></div>
        )}
        <div className="lu-body">
          {(step === 'flap1' || step === 'flap2') && (<>
            <div className="lu-q" dangerouslySetInnerHTML={{ __html: ct(step === 'flap1' ? 'luWhich2' : 'luWhich3') }} />
            <p className="lu-qs">{ct('luAdds', { d: D })}</p>
            {preview}
            {flapGrid}
          </>)}
          {step === 'extras' && (<>
            <div className="lu-q">{ct('luMore', { d: D })}</div>
            <p className="lu-qs">{ct('luOptional')}</p>
            <div className="lu-tabs">
              {data.extras.map(c => {
                const n = extras.filter(e => e.catId === c.id).length
                return <button key={c.id} type="button" className="lu-tab" aria-pressed={c.id === tab} onClick={() => setTab(c.id)}>{cterm(c.name)}{n ? <span className="n">({n})</span> : null}</button>
              })}
            </div>
            <div className="lu-grid">
              {cat?.products.map(p => {
                const on = extras.some(e => e.p.id === p.id)
                return (
                  <button key={p.id} type="button" className="lu-opt" aria-pressed={on}
                    onClick={() => setExtras(prev => on ? prev.filter(e => e.p.id !== p.id) : [...prev, { catId: cat.id, p }])}>
                    <span className="chk" aria-hidden="true">✓</span>
                    {(p.thumb || p.image) && <img src={p.thumb || p.image} alt="" loading="lazy" />}
                    {cterm(p.name)}
                    <span className="pr"><s>{fmt(p.price)}</s>{fmt(off(p.price))}</span>
                  </button>
                )
              })}
            </div>
          </>)}
          {step === 'recap' && (
            <div className="lu-recap">
              <div className="lu-line"><span><span><span className="k">{ct('luYourBag')}</span>{[cterm(ctx.baseName), ...ctx.selectedProducts.filter(p => p.id !== myFlap?.id).map(p => label(p.name))].filter(Boolean).join(' · ')}</span></span><span className="p"><s>{fmt(ctx.totalPrice - (myFlap?.price || 0))}</s>{fmt(data.bagNow - off(myFlap?.price || 0) + coreDelta)}</span></div>
              {[myFlap, ...picks].map((p, i) => p && (
                <div className="lu-line" key={'f' + i}><span>{p.thumb && <img src={p.thumb} alt="" />}<span><span className="k">{ct(i === 0 ? 'luFlap1' : i === 1 ? 'luFlap2' : 'luFlap3')}</span>{label(p.name)}</span></span><span className="p"><s>{fmt(p.price)}</s>{fmt(off(p.price))}</span></div>
              ))}
              {extras.map(e => (
                <div className="lu-line" key={e.p.id}><span>{(e.p.thumb || e.p.image) && <img src={e.p.thumb || e.p.image} alt="" />}<span><span className="k">{cterm(data.extras.find(c => c.id === e.catId)?.name)}</span>{cterm(e.p.name)}</span></span><span className="p"><s>{fmt(e.p.price)}</s>{fmt(off(e.p.price))}<button type="button" className="lu-rm" onClick={() => setExtras(prev => prev.filter(x => x.p.id !== e.p.id))}>{ct('luRemove')}</button></span></div>
              ))}
              <div className="lu-tot"><span>{ct('luWithout')}<s>{fmt(before)}</s></span><span className="big">{ct('luLotPrice', { price: fmt(now) })}</span><span>{ct('luSave', { amount: fmt(before - now) })}</span></div>
            </div>
          )}
          {step === 'done' && (
            <div className="lu-done"><div className="ok">✓</div><h3>{ct('luDone')}</h3><p>{ct('luDoneText', { amount: fmt(before - now) })}</p>
              <button type="button" className="lu-btn" onClick={onClose}>{ct('continue')}</button></div>
          )}
        </div>
        {step !== 'done' && (
          <div className="lu-foot">
            {step === 'flap1' && <button type="button" className="lu-btn accent" disabled={!picks[0]} onClick={() => setStep('flap2')}>{ct('luNextFlap')}</button>}
            {step === 'flap2' && <div className="row"><button type="button" className="lu-btn ghost" onClick={() => setStep('flap1')}>{ct('luBack')}</button><button type="button" className="lu-btn accent" disabled={!picks[1]} onClick={() => setStep('extras')}>{ct('luNext')}</button></div>}
            {step === 'extras' && <div className="row"><button type="button" className="lu-btn ghost" onClick={() => setStep('flap2')}>{ct('luBack')}</button><button type="button" className="lu-btn accent" onClick={() => setStep('recap')}>{extras.length ? ct('luRecapN', { n: extras.length }) : ct('luNoThanks')}</button></div>}
            {step === 'recap' && <div className="row"><button type="button" className="lu-btn ghost" onClick={() => setStep('extras')}>{ct('luBack')}</button><button type="button" className="lu-btn accent" disabled={busy} onClick={addToCart}>{busy ? ct('adding') : ct('luAdd', { price: fmt(now) })}</button></div>}
          </div>
        )}
      </div>
    </>
  )
}

export { track as trackLotUpsell }

/** Emplacement « après les choix » du configurateur : l'encart, et le panneau quand il est ouvert. */
export function LotUpsellSlot({ ctx, configSlug, wcAjaxUrl, wcNonce, open, setOpen }: {
  ctx: ConfiguratorSlotContext; configSlug: string; wcAjaxUrl?: string; wcNonce?: string; open: boolean; setOpen: (v: boolean) => void
}) {
  const data = useLotUpsell(ctx)
  const openPanel = () => { track('config_lot_ouvert', { configurateur: configSlug, depuis: 'encart' }); setOpen(true) }
  return (
    <>
      <LotUpsellCard ctx={ctx} data={data} onOpen={openPanel} />
      {open && data && data.myFlap && (
        <LotUpsellPanel ctx={ctx} data={data} configSlug={configSlug} wcAjaxUrl={wcAjaxUrl} wcNonce={wcNonce} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

/** Emplacement « au-dessus d'Acheter » : le rappel. */
export function LotUpsellHintSlot({ ctx, configSlug, setOpen }: { ctx: ConfiguratorSlotContext; configSlug: string; setOpen: (v: boolean) => void }) {
  const data = useLotUpsell(ctx)
  return <LotUpsellHint ctx={ctx} data={data} onOpen={() => { track('config_lot_ouvert', { configurateur: configSlug, depuis: 'rappel' }); setOpen(true) }} />
}
